import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import {
  Clock, UserCircle, Calendar, Envelope, EnvelopeSimple, Handshake, Trophy, FileText,
  Pause, Play, Warning, Sparkle,
} from "@phosphor-icons/react";

const KIND_META = {
  assignment: { icon: UserCircle, color: "bg-sky-100 text-sky-700" },
  evaluation: { icon: Calendar, color: "bg-violet-100 text-violet-700" },
  "evaluation-completed": { icon: Sparkle, color: "bg-emerald-100 text-emerald-700" },
  application: { icon: FileText, color: "bg-blue-100 text-blue-700" },
  "cold-email": { icon: EnvelopeSimple, color: "bg-teal-100 text-teal-700" },
  interview: { icon: Handshake, color: "bg-indigo-100 text-indigo-700" },
  offer: { icon: Trophy, color: "bg-emerald-100 text-emerald-700" },
  document: { icon: FileText, color: "bg-slate-100 text-slate-700" },
  hold: { icon: Pause, color: "bg-amber-100 text-amber-700" },
  resume: { icon: Play, color: "bg-emerald-100 text-emerald-700" },
  complaint: { icon: Warning, color: "bg-red-100 text-red-700" },
};

const ALL_KINDS = Object.keys(KIND_META);

export default function CandidateTimeline({ candidateId }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get(`/candidates/${candidateId}/timeline`).then((r) => setData(r.data));
  }, [candidateId]);

  const events = useMemo(() => {
    if (!data?.events) return [];
    if (filter === "all") return data.events;
    return data.events.filter((e) => e.kind === filter || (filter === "evaluation" && e.kind === "evaluation-completed"));
  }, [data, filter]);

  const counts = useMemo(() => {
    const c = {};
    (data?.events || []).forEach((e) => { c[e.kind] = (c[e.kind] || 0) + 1; });
    return c;
  }, [data]);

  const visibleKinds = useMemo(() => ALL_KINDS.filter((k) => counts[k]), [counts]);

  if (!data) return <div className="text-slate-500 text-sm">Loading timeline…</div>;

  return (
    <div className="space-y-3" data-testid="candidate-timeline">
      <div className="flex items-center gap-2 flex-wrap">
        <button data-testid="timeline-filter-all"
                onClick={() => setFilter("all")}
                className={`text-xs px-2.5 py-1 rounded-full border ${filter === "all" ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          All · {data.events.length}
        </button>
        {visibleKinds.map((k) => {
          const M = KIND_META[k];
          return (
            <button key={k} data-testid={`timeline-filter-${k}`}
                    onClick={() => setFilter(k)}
                    className={`text-xs px-2.5 py-1 rounded-full border flex items-center gap-1 ${filter === k ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              <M.icon size={11} /> {k} · {counts[k]}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        {events.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No events yet.</div>}
        <ol className="relative border-l-2 border-slate-100 space-y-4 ml-2">
          {events.map((ev, idx) => {
            const M = KIND_META[ev.kind] || { icon: Clock, color: "bg-slate-100 text-slate-700" };
            const eventKey = `${ev.date || idx}-${ev.kind || ""}-${ev.title || ""}`;
            return (
              <li key={eventKey} className="pl-6 relative" data-testid={`timeline-event-${idx}`}>
                <span className={`absolute -left-3 top-0.5 grid place-items-center rounded-full w-6 h-6 ${M.color}`}>
                  <M.icon size={12} weight="bold" />
                </span>
                <div className="text-xs text-slate-400">{new Date(ev.date).toLocaleString()}</div>
                <div className="text-sm font-medium text-slate-800">{ev.title}</div>
                {ev.detail && <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-line">{ev.detail}</div>}
                {ev.actor && <div className="text-[10px] text-slate-400 mt-0.5">by {ev.actor}</div>}
                {ev.meta?.url && (
                  <a href={ev.meta.url} target="_blank" rel="noreferrer" className="text-[11px] text-sky-700 hover:underline">Open link →</a>
                )}
                {ev.meta?.status && (
                  <span className="ml-2 text-[10px] text-slate-500">· {ev.meta.status}</span>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
